"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { ProtectedRoute } from "@/components/protected-route"
import { Sidebar } from "@/components/sidebar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { cn } from "@/lib/utils"
import {
  CheckCircle2, X, RefreshCw, Trash2, AlertCircle,
  WhatsappLogo, MessageSquare, Users, MessageCircle,
  Send, Phone, Copy, QrCode, PlugConnected,
  PlugDisconnected, Loader2, Plus, Smartphone,
  Activity, Shield, Globe2, Zap,
} from "@/lib/icons"
import { getEvolutionApiUrl, getEvolutionApiKey } from "@/lib/admin-config"
import {
  getIaPausadaAll,
  setIaPausadaAll,
  subscribeToIaPausadaAll,
  getIaPausadaForInstance,
  setIaPausadaForInstance,
  INSTANCE_TO_IA_KEY,
} from "@/lib/supabase"
import { Bot, Pause } from "@/lib/icons"

// =============================================
// EVOLUTION API v2 HELPERS
// =============================================

const BOT_WEBHOOK_EVENTS = ["MESSAGES_UPSERT"]

async function evoFetch(path, { method = "GET", body = null } = {}) {
  const baseUrl = getEvolutionApiUrl()?.replace(/\/+$/, "")
  const apiKey = getEvolutionApiKey()
  if (!baseUrl || !apiKey) throw new Error("Evolution API no configurada. Ve a Configuracion primero.")
  const opts = { method, headers: { "Content-Type": "application/json", apikey: apiKey } }
  if (body) opts.body = JSON.stringify(body)
  const res = await fetch(`${baseUrl}${path}`, opts)
  if (!res.ok) { const text = await res.text().catch(() => ""); throw new Error(`Error ${res.status}: ${text}`) }
  return res.json()
}

const api = {
  fetchInstances: () => evoFetch("/instance/fetchInstances"),
  connectionState: (n) => evoFetch(`/instance/connectionState/${encodeURIComponent(n)}`),
  connect: (n) => evoFetch(`/instance/connect/${encodeURIComponent(n)}`),
  create: (body) => evoFetch("/instance/create", { method: "POST", body }),
  delete: (n) => evoFetch(`/instance/delete/${encodeURIComponent(n)}`, { method: "DELETE" }),
  logout: (n) => evoFetch(`/instance/logout/${encodeURIComponent(n)}`, { method: "DELETE" }),
  restart: (n) => evoFetch(`/instance/restart/${encodeURIComponent(n)}`, { method: "PUT" }),
  sendText: (n, number, text) => evoFetch(`/message/sendText/${encodeURIComponent(n)}`, { method: "POST", body: { number, text, delay: 1200 } }),
  // Webhook control — activar/desactivar sin tocar Evolution admin
  getWebhook: (n) => evoFetch(`/webhook/find/${encodeURIComponent(n)}`),
  setWebhook: (n, body) => evoFetch(`/webhook/set/${encodeURIComponent(n)}`, { method: "POST", body }),
  profilePic: async (instanceName, ownerJid) => {
    const number = ownerJid?.replace(/@.*$/, "") || ""
    if (!number) return null
    try {
      const d = await evoFetch(`/chat/fetchProfilePictureUrl/${encodeURIComponent(instanceName)}`, { method: "POST", body: { number } })
      const url = d?.profilePictureUrl || d?.url || d?.picture || null
      if (url) return url
    } catch { /* try next */ }
    try {
      const d = await evoFetch(`/chat/fetchProfilePictureUrl/${encodeURIComponent(instanceName)}`, { method: "POST", body: { number: ownerJid } })
      const url = d?.profilePictureUrl || d?.url || d?.picture || null
      if (url) return url
    } catch { /* try next */ }
    try {
      const d = await evoFetch(`/chat/fetchProfile/${encodeURIComponent(instanceName)}`, { method: "POST", body: { number } })
      const url = d?.picture || d?.profilePictureUrl || d?.imgUrl || null
      if (url) return url
    } catch { /* give up */ }
    return null
  },
}

const parse = {
  name: (i) => i?.instance?.instanceName || i?.instance?.name || i?.instanceName || i?.name || null,
  state: (i) => i?.instance?.state || i?.instance?.connectionStatus || i?.instance?.status || i?.state || i?.connectionStatus || null,
  owner: (i) => i?.instance?.owner || i?.instance?.ownerJid || i?.owner || null,
  pic: (i) => i?.instance?.profilePictureUrl || i?.instance?.profilePicUrl || i?.profilePictureUrl || i?.profilePicUrl || null,
  profileName: (i) => i?.instance?.profileName || i?.profileName || null,
  integration: (i) => i?.instance?.integration || i?.integration || "WHATSAPP-BAILEYS",
  counts: (i) => i?._count || i?.instance?._count || null,
}

function formatPhone(jid) {
  if (!jid) return null
  const num = jid.replace(/@s\.whatsapp\.net$/, "")
  const m = num.match(/^(\d{2})(\d{2,4})(\d{4})(\d{0,4})$/)
  return m ? `+${m[1]} ${m[2]} ${m[3]}${m[4] ? " " + m[4] : ""}` : num
}

// =============================================
// INSTANCE CARD — Premium redesign
// =============================================

function InstanceCard({ instance, onConnect }) {
  // Las URLs de pps.whatsapp.net expiran (403): si falla la imagen, caemos al logo
  const [picError, setPicError] = useState(false)
  useEffect(() => { setPicError(false) }, [instance.profilePic])
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showSendMsg, setShowSendMsg] = useState(false)
  const [msgNumber, setMsgNumber] = useState("")
  const [msgText, setMsgText] = useState("")
  const [sending, setSending] = useState(false)
  const [restarting, setRestarting] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  // Webhook toggle state
  const [webhookEnabled, setWebhookEnabled] = useState(null) // null = cargando
  const [webhookConfig, setWebhookConfig] = useState(null)   // guarda url+events para re-enable
  const [togglingWebhook, setTogglingWebhook] = useState(false)
  const [showWebhookConfirm, setShowWebhookConfirm] = useState(false)

  // IA por instancia toggle state
  const iaSupported = Boolean(INSTANCE_TO_IA_KEY[instance?.name])
  const [iaActiva, setIaActiva] = useState(null) // null = cargando / no soportada
  const [togglingIa, setTogglingIa] = useState(false)
  const [showIaConfirm, setShowIaConfirm] = useState(false)

  // Cargar estado IA de esta instancia
  useEffect(() => {
    let cancelled = false
    if (!iaSupported) { setIaActiva(null); return }
    getIaPausadaForInstance(instance.name)
      .then((pausada) => { if (!cancelled) setIaActiva(!pausada) })
      .catch(() => { if (!cancelled) setIaActiva(false) })
    return () => { cancelled = true }
  }, [instance?.name, iaSupported])

  const handleToggleIa = async () => {
    setTogglingIa(true)
    try {
      const nextPausada = iaActiva // si está activa, ahora la pausamos
      await setIaPausadaForInstance(instance.name, nextPausada)
      setIaActiva(!nextPausada)
      toast.success(nextPausada
        ? `IA pausada para ${instance.name} — el bot no responderá`
        : `IA activada para ${instance.name} — el bot va a responder`)
      setShowIaConfirm(false)
    } catch (e) {
      toast.error("Error: " + e.message)
    } finally {
      setTogglingIa(false)
    }
  }

  // Cargar estado actual del webhook al montar
  useEffect(() => {
    let cancelled = false
    if (!instance?.name) return
    api.getWebhook(instance.name)
      .then((res) => {
        if (cancelled) return
        // Evolution responde distinto según versión: a veces { enabled, url, events } directo,
        // a veces { webhook: { enabled, url, events } }
        const wh = res?.webhook || res
        setWebhookEnabled(Boolean(wh?.enabled))
        setWebhookConfig({
          url: wh?.url || "",
          events: wh?.events || [],
          webhookByEvents: wh?.webhookByEvents ?? wh?.webhook_by_events ?? false,
          webhookBase64: wh?.webhookBase64 ?? wh?.webhook_base64 ?? false,
        })
      })
      .catch(() => { if (!cancelled) setWebhookEnabled(false) })
    return () => { cancelled = true }
  }, [instance?.name])

  const handleToggleWebhook = async () => {
    setTogglingWebhook(true)
    try {
      const newState = !webhookEnabled
      await api.setWebhook(instance.name, {
        webhook: {
          enabled: newState,
          url: webhookConfig?.url || "",
          events: BOT_WEBHOOK_EVENTS,
          webhookByEvents: false,
          webhookBase64: false,
        },
      })
      setWebhookEnabled(newState)
      setWebhookConfig((prev) => ({ ...(prev || {}), events: BOT_WEBHOOK_EVENTS, webhookByEvents: false, webhookBase64: false }))
      toast.success(newState ? "Bot ACTIVADO — los mensajes se procesan" : "Bot PAUSADO — mensajes no llegan a n8n")
      setShowWebhookConfirm(false)
    } catch (e) {
      toast.error("Error: " + e.message)
    } finally {
      setTogglingWebhook(false)
    }
  }

  const isConnected = instance.state === "open"
  const isConnecting = instance.state === "connecting"
  const isClosed = !isConnected && !isConnecting

  const phone = formatPhone(instance.owner)
  const integration = (instance.integration || "WHATSAPP-BAILEYS").replace("WHATSAPP-", "")
  const c = instance.counts || {}

  const copyName = () => { navigator.clipboard.writeText(instance.name); toast.success("Nombre copiado") }

  const handleRestart = async () => {
    setRestarting(true)
    try { await api.restart(instance.name); toast.success("Instancia reiniciada") }
    catch (e) { toast.error(e.message) }
    finally { setRestarting(false) }
  }

  const handleLogout = async () => {
    setLoggingOut(true)
    try { await api.logout(instance.name); toast.success("Sesion cerrada"); instance.onRefresh?.() }
    catch (e) { toast.error(e.message) }
    finally { setLoggingOut(false) }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try { await api.delete(instance.name); toast.success(`"${instance.name}" eliminada`); setShowDelete(false); instance.onRefresh?.() }
    catch (e) { toast.error(e.message) }
    finally { setDeleting(false) }
  }

  const handleSendMsg = async () => {
    if (!msgNumber.trim() || !msgText.trim()) return
    setSending(true)
    try {
      await api.sendText(instance.name, msgNumber.replace(/\D/g, ""), msgText)
      toast.success("Mensaje enviado"); setShowSendMsg(false); setMsgNumber(""); setMsgText("")
    } catch (e) { toast.error(e.message) }
    finally { setSending(false) }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={cn(
          "relative rounded-2xl border overflow-hidden transition-all duration-300 group backdrop-blur-xl",
          "hover:shadow-2xl hover:-translate-y-1",
          isConnected && "border-emerald-400/40 bg-gradient-to-br from-white/80 via-white/70 to-emerald-50/60 dark:from-slate-900/80 dark:via-slate-900/70 dark:to-emerald-950/40 shadow-lg shadow-emerald-500/10 hover:shadow-emerald-500/30 dark:hover:shadow-emerald-500/20",
          isConnecting && "border-amber-400/40 bg-gradient-to-br from-white/80 via-white/70 to-amber-50/60 dark:from-slate-900/80 dark:via-slate-900/70 dark:to-amber-950/40 shadow-lg shadow-amber-500/10 hover:shadow-amber-500/25",
          isClosed && "border-border/60 bg-white/70 dark:bg-slate-900/70 hover:shadow-slate-500/10"
        )}
      >
        {/* Decorative glow para conectadas */}
        {isConnected && (
          <>
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: "4s" }} />
            <div className="absolute -bottom-20 -left-20 w-32 h-32 bg-teal-500/15 rounded-full blur-3xl pointer-events-none" />
          </>
        )}
        {isConnecting && (
          <div className="absolute -top-16 -right-16 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl pointer-events-none animate-pulse" />
        )}

        {/* Status accent bar */}
        <div className={cn(
          "h-1 transition-all",
          isConnected && "bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-400",
          isConnecting && "bg-gradient-to-r from-amber-400 via-amber-500 to-orange-400 animate-pulse",
          isClosed && "bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700"
        )} />

        <div className="relative p-5">
          {/* ── Header: Avatar grande + nombre + badge ── */}
          <div className="flex items-start gap-4">
            <div className={cn(
              "relative flex h-16 w-16 items-center justify-center rounded-2xl shrink-0 transition-all overflow-hidden",
              isConnected ? "ring-2 ring-emerald-500/40 bg-gradient-to-br from-emerald-400/20 to-teal-500/20 shadow-lg shadow-emerald-500/20"
                : isConnecting ? "ring-2 ring-amber-500/40 bg-amber-500/10 shadow-lg shadow-amber-500/15"
                : "ring-1 ring-border/40 bg-muted/30"
            )}>
              {instance.profilePic && !picError ? (
                <img src={instance.profilePic} alt={instance.profileName || instance.name} onError={() => setPicError(true)} className="h-16 w-16 rounded-2xl object-cover" />
              ) : (
                <WhatsappLogo className={cn(
                  "h-8 w-8 transition-colors",
                  isConnected ? "text-emerald-500" : isConnecting ? "text-amber-500" : "text-muted-foreground/40"
                )} />
              )}
              {isConnected && (
                <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 border-2 border-card shadow-md flex items-center justify-center">
                  <CheckCircle2 className="h-2.5 w-2.5 text-white" />
                </div>
              )}
              {isConnecting && (
                <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-amber-500 border-2 border-card shadow-md animate-pulse flex items-center justify-center">
                  <Loader2 className="h-2.5 w-2.5 text-white animate-spin" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="text-base font-bold truncate tracking-tight">{instance.profileName || instance.name}</h3>
                <button
                  onClick={copyName}
                  className="opacity-40 hover:opacity-100 transition-all hover:scale-110 p-1 rounded-md hover:bg-muted/50 shrink-0"
                  title="Copiar nombre"
                >
                  <Copy className="h-3 w-3 text-muted-foreground" />
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground/60 font-mono tracking-wide truncate">{instance.name}</p>

              {/* Phone + Integration en una sola fila */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {instance.owner && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                    <Phone className="h-3.5 w-3.5 text-emerald-500/70" />
                    <span className="tabular-nums">{phone || instance.owner.split("@")[0]}</span>
                  </span>
                )}
                <span className={cn(
                  "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                  integration === "Baileys"
                    ? "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400"
                    : "bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400"
                )}>
                  <Shield className="h-2.5 w-2.5" />
                  {integration}
                </span>
              </div>
            </div>

            {/* Status badge prominente */}
            <Badge variant="outline" className={cn(
              "shrink-0 text-[10.5px] gap-1.5 font-bold rounded-full px-3 py-1 transition-all",
              isConnected && "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40 shadow-sm shadow-emerald-500/10",
              isConnecting && "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40 animate-pulse",
              isClosed && "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30"
            )}>
              {isConnected ? (<><div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Conectado</>)
                : isConnecting ? (<><Loader2 className="h-3 w-3 animate-spin" /> Conectando</>)
                : (<><div className="h-1.5 w-1.5 rounded-full bg-red-500" /> Desconectado</>)}
            </Badge>
          </div>

          {/* ── Stats grid: números grandes ── */}
          {(c.msgs != null || c.contacts != null || c.chats != null) && (
            <div className="mt-5 grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-800/30 p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <MessageSquare className="h-3 w-3 text-emerald-500/70" />
                  <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700/70 dark:text-emerald-400/70">Mensajes</span>
                </div>
                <p className="text-lg font-black tabular-nums text-emerald-700 dark:text-emerald-300">
                  {(c.msgs ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="rounded-xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-800/30 p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <Users className="h-3 w-3 text-blue-500/70" />
                  <span className="text-[9px] uppercase tracking-wider font-bold text-blue-700/70 dark:text-blue-400/70">Contactos</span>
                </div>
                <p className="text-lg font-black tabular-nums text-blue-700 dark:text-blue-300">
                  {(c.contacts ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="rounded-xl bg-violet-50/60 dark:bg-violet-950/20 border border-violet-200/60 dark:border-violet-800/30 p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <MessageCircle className="h-3 w-3 text-violet-500/70" />
                  <span className="text-[9px] uppercase tracking-wider font-bold text-violet-700/70 dark:text-violet-400/70">Chats</span>
                </div>
                <p className="text-lg font-black tabular-nums text-violet-700 dark:text-violet-300">
                  {(c.chats ?? 0).toLocaleString()}
                </p>
              </div>
            </div>
          )}

          {/* ── Bot Toggle: switch real ── */}
          {webhookEnabled !== null && (
            <div className={cn(
              "mt-4 rounded-xl border p-3 flex items-center justify-between transition-all",
              webhookEnabled
                ? "border-emerald-300/60 dark:border-emerald-700/40 bg-emerald-50/60 dark:bg-emerald-950/20"
                : "border-red-300/60 dark:border-red-700/40 bg-red-50/60 dark:bg-red-950/20"
            )}>
              <div className="flex items-center gap-2.5">
                <div className={cn(
                  "h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
                  webhookEnabled ? "bg-emerald-500 shadow-md shadow-emerald-500/30" : "bg-slate-300 dark:bg-slate-700"
                )}>
                  <Zap className={cn("h-4 w-4", webhookEnabled ? "text-white" : "text-slate-500")} />
                </div>
                <div>
                  <p className={cn(
                    "text-sm font-bold leading-tight",
                    webhookEnabled ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"
                  )}>
                    Bot {webhookEnabled ? "Activo" : "Pausado"}
                  </p>
                  <p className="text-[10px] text-muted-foreground/70 leading-tight mt-0.5">
                    {webhookEnabled ? "Procesando mensajes" : "No responde mensajes"}
                  </p>
                </div>
              </div>
              {/* Toggle visual real */}
              <button
                onClick={() => setShowWebhookConfirm(true)}
                disabled={togglingWebhook}
                className={cn(
                  "relative h-7 w-12 rounded-full transition-all shadow-inner shrink-0",
                  webhookEnabled
                    ? "bg-emerald-500"
                    : "bg-slate-300 dark:bg-slate-700",
                  togglingWebhook && "opacity-50 cursor-wait"
                )}
                title={webhookEnabled ? "Click para PAUSAR" : "Click para ACTIVAR"}
              >
                <div className={cn(
                  "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all flex items-center justify-center",
                  webhookEnabled ? "left-[22px]" : "left-0.5"
                )}>
                  {togglingWebhook ? (
                    <Loader2 className="h-3 w-3 animate-spin text-slate-500" />
                  ) : webhookEnabled ? (
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  ) : null}
                </div>
              </button>
            </div>
          )}

          {/* ── IA Activa toggle: solo para instancias con workflow asociado ── */}
          {iaSupported && iaActiva !== null && (
            <div className={cn(
              "mt-3 rounded-xl border p-3 flex items-center justify-between transition-all",
              iaActiva
                ? "border-indigo-300/60 dark:border-indigo-700/40 bg-indigo-50/60 dark:bg-indigo-950/20"
                : "border-amber-300/60 dark:border-amber-700/40 bg-amber-50/60 dark:bg-amber-950/20"
            )}>
              <div className="flex items-center gap-2.5">
                <div className={cn(
                  "h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
                  iaActiva ? "bg-indigo-500 shadow-md shadow-indigo-500/30" : "bg-slate-300 dark:bg-slate-700"
                )}>
                  {iaActiva
                    ? <Bot className="h-4 w-4 text-white" />
                    : <Pause className="h-4 w-4 text-slate-500" />}
                </div>
                <div>
                  <p className={cn(
                    "text-sm font-bold leading-tight",
                    iaActiva ? "text-indigo-700 dark:text-indigo-300" : "text-amber-700 dark:text-amber-300"
                  )}>
                    IA {iaActiva ? "Activa" : "Pausada"}
                  </p>
                  <p className="text-[10px] text-muted-foreground/70 leading-tight mt-0.5">
                    {iaActiva ? "El bot responde automáticamente" : "El bot NO responde, solo guarda"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowIaConfirm(true)}
                disabled={togglingIa}
                className={cn(
                  "relative h-7 w-12 rounded-full transition-all shadow-inner shrink-0",
                  iaActiva ? "bg-indigo-500" : "bg-slate-300 dark:bg-slate-700",
                  togglingIa && "opacity-50 cursor-wait"
                )}
                title={iaActiva ? "Click para PAUSAR IA" : "Click para ACTIVAR IA"}
              >
                <div className={cn(
                  "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all flex items-center justify-center",
                  iaActiva ? "left-[22px]" : "left-0.5"
                )}>
                  {togglingIa
                    ? <Loader2 className="h-3 w-3 animate-spin text-slate-500" />
                    : iaActiva
                      ? <CheckCircle2 className="h-3 w-3 text-indigo-500" />
                      : null}
                </div>
              </button>
            </div>
          )}

          {/* ── Acciones principales — botones grandes con iconos visibles ── */}
          <div className="mt-4 flex items-center gap-2 flex-wrap sm:flex-nowrap">
            {!isConnected ? (
              <button
                onClick={() => onConnect(instance.name)}
                className="flex-1 min-w-[160px] h-10 px-4 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all active:scale-[0.98] shadow-sm"
              >
                <QrCode className="h-4 w-4" /> Conectar WhatsApp
              </button>
            ) : (
              <button
                onClick={() => setShowSendMsg(true)}
                className="flex-1 min-w-[160px] h-10 px-4 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all active:scale-[0.98] shadow-sm"
              >
                <Send className="h-4 w-4" /> Enviar mensaje
              </button>
            )}

            <button
              onClick={handleRestart}
              disabled={restarting}
              className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors disabled:opacity-50 shrink-0"
              title="Reiniciar instancia"
            >
              <RefreshCw className={cn("h-4 w-4", restarting && "animate-spin")} />
            </button>

            {isConnected && (
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/30 dark:hover:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/40 transition-colors disabled:opacity-50 shrink-0"
                title="Cerrar sesión"
              >
                <PlugDisconnected className="h-4 w-4" />
              </button>
            )}

            <button
              onClick={() => setShowDelete(true)}
              className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:hover:bg-red-950/50 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40 transition-colors shrink-0"
              title="Eliminar instancia"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Delete dialog */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-2xl bg-red-500/10 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <AlertDialogTitle className="text-base">Eliminar instancia</AlertDialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5 font-mono">{instance.name}</p>
              </div>
            </div>
            <AlertDialogDescription className="text-sm">
              Se perdera la sesion de WhatsApp y toda la configuracion de esta instancia. Esta accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 text-white hover:bg-red-700 rounded-xl gap-2">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? "Eliminando..." : "Eliminar definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Toggle webhook confirmation dialog */}
      <AlertDialog open={showWebhookConfirm} onOpenChange={setShowWebhookConfirm}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className={cn(
                "h-12 w-12 rounded-2xl flex items-center justify-center",
                webhookEnabled ? "bg-red-500/10" : "bg-emerald-500/10"
              )}>
                <Zap className={cn("h-6 w-6", webhookEnabled ? "text-red-500" : "text-emerald-500")} />
              </div>
              <div>
                <AlertDialogTitle className="text-base">
                  {webhookEnabled ? "¿Pausar el bot?" : "¿Activar el bot?"}
                </AlertDialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5 font-mono">{instance.name}</p>
              </div>
            </div>
            <AlertDialogDescription className="text-sm space-y-2">
              {webhookEnabled ? (
                <>
                  <span className="block">
                    Desactivar el webhook pausa TODO el flujo de n8n. Los mensajes que lleguen
                    <b> no se procesan, no se guardan en el CRM, y el bot no responde</b>.
                  </span>
                  <span className="block text-amber-600 dark:text-amber-400 font-medium">
                    Úsalo solo en emergencias. Reactiva lo antes posible para no perder prospectos.
                  </span>
                </>
              ) : (
                <>
                  <span className="block">
                    Reactivar el webhook reanuda el flujo normal: los mensajes entrantes
                    vuelven a procesarse por n8n, se guardan en el CRM y el bot responde.
                  </span>
                  <span className="block text-emerald-600 dark:text-emerald-400 font-medium">
                    Los mensajes que llegaron mientras estaba pausado NO se recuperan.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleWebhook}
              disabled={togglingWebhook}
              className={cn(
                "rounded-xl gap-2 text-white",
                webhookEnabled
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-emerald-600 hover:bg-emerald-700"
              )}
            >
              {togglingWebhook ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              {togglingWebhook
                ? "Aplicando..."
                : webhookEnabled
                  ? "Sí, pausar el bot"
                  : "Sí, activar el bot"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Toggle IA por instancia confirmation dialog */}
      <AlertDialog open={showIaConfirm} onOpenChange={setShowIaConfirm}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className={cn(
                "h-12 w-12 rounded-2xl flex items-center justify-center",
                iaActiva ? "bg-amber-500/10" : "bg-indigo-500/10"
              )}>
                {iaActiva
                  ? <Pause className="h-6 w-6 text-amber-500" />
                  : <Bot className="h-6 w-6 text-indigo-500" />}
              </div>
              <div>
                <AlertDialogTitle className="text-base">
                  {iaActiva ? "¿Pausar la IA?" : "¿Activar la IA?"}
                </AlertDialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5 font-mono">{instance.name}</p>
              </div>
            </div>
            <AlertDialogDescription className="text-sm space-y-2">
              {iaActiva ? (
                <>
                  <span className="block">
                    El bot dejará de responder a los prospectos de <b>{instance.name}</b>.
                    Los mensajes <b>se siguen guardando</b> en el CRM para que las CAPs atiendan a mano.
                  </span>
                  <span className="block text-amber-600 dark:text-amber-400 font-medium">
                    Solo afecta a esta instancia. Las demás siguen como estén.
                  </span>
                </>
              ) : (
                <>
                  <span className="block">
                    El bot volverá a responder automáticamente a los prospectos de <b>{instance.name}</b>
                    siguiendo el flujo del workflow.
                  </span>
                  <span className="block text-indigo-600 dark:text-indigo-400 font-medium">
                    Solo afecta a esta instancia. Las demás siguen como estén.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleIa}
              disabled={togglingIa}
              className={cn(
                "rounded-xl gap-2 text-white",
                iaActiva ? "bg-amber-600 hover:bg-amber-700" : "bg-indigo-600 hover:bg-indigo-700"
              )}
            >
              {togglingIa
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : iaActiva ? <Pause className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
              {togglingIa
                ? "Aplicando..."
                : iaActiva ? "Sí, pausar IA" : "Sí, activar IA"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send message dialog */}
      <Dialog open={showSendMsg} onOpenChange={setShowSendMsg}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-base">
              <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Send className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="font-bold">Enviar mensaje</p>
                <p className="text-xs text-muted-foreground font-normal font-mono mt-0.5">{instance.name}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold">Numero (con codigo de pais)</Label>
              <Input placeholder="5215512345678" value={msgNumber} onChange={(e) => setMsgNumber(e.target.value)} className="mt-1.5 h-10 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Mensaje</Label>
              <textarea
                placeholder="Escribe tu mensaje..."
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50 transition-all"
              />
            </div>
            <Button
              onClick={handleSendMsg}
              disabled={sending || !msgNumber.trim() || !msgText.trim()}
              className="w-full h-11 gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-600/20 font-semibold"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Enviando..." : "Enviar mensaje"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// =============================================
// CREATE INSTANCE DIALOG
// =============================================

function CreateInstanceDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("")
  const [number, setNumber] = useState("")
  const [integration, setIntegration] = useState("WHATSAPP-BAILEYS")
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) return
    setCreating(true)
    try {
      const result = await api.create({
        instanceName: name.trim().replace(/\s+/g, "-").toLowerCase(),
        number: number.trim() || undefined,
        integration,
        qrcode: true,
        rejectCall: false,
        groupsIgnore: true,
        alwaysOnline: false,
        readMessages: false,
        readStatus: false,
      })
      toast.success(`Instancia "${name}" creada`)
      onOpenChange(false)
      setName(""); setNumber("")
      onCreated?.(result)
    } catch (e) { toast.error(e.message) }
    finally { setCreating(false) }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-base">
            <div className="h-11 w-11 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <WhatsappLogo className="h-6 w-6 text-emerald-500" />
            </div>
            <div>
              <p className="font-bold">Nueva instancia</p>
              <p className="text-xs text-muted-foreground font-normal mt-0.5">Configura tu conexion de WhatsApp</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-3">
          <div>
            <Label className="text-xs font-semibold">Nombre de la instancia <span className="text-red-400">*</span></Label>
            <Input
              placeholder="mi-whatsapp"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 h-10 rounded-xl"
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              autoFocus
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">Solo letras, numeros y guiones. Sin espacios.</p>
          </div>

          <div>
            <Label className="text-xs font-semibold">Numero de telefono (opcional)</Label>
            <Input placeholder="5215512345678" value={number} onChange={(e) => setNumber(e.target.value)} className="mt-1.5 h-10 rounded-xl" />
            <p className="text-[10px] text-muted-foreground mt-1.5">Con codigo de pais, sin + ni espacios</p>
          </div>

          <div>
            <Label className="text-xs font-semibold">Integracion</Label>
            <select
              value={integration}
              onChange={(e) => setIntegration(e.target.value)}
              className="mt-1.5 w-full h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50 transition-all"
            >
              <option value="WHATSAPP-BAILEYS">WhatsApp Baileys (recomendado)</option>
              <option value="WHATSAPP-BUSINESS">WhatsApp Business API</option>
            </select>
          </div>

          <Button
            onClick={handleCreate}
            disabled={creating || !name.trim()}
            className="w-full h-11 gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-600/20 font-semibold"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {creating ? "Creando instancia..." : "Crear instancia"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// =============================================
// CONNECT DIALOG (QR + pairing code)
// =============================================

function ConnectDialog({ instanceName, open, onOpenChange }) {
  const [connectData, setConnectData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const pollRef = useRef(null)

  const fetchQR = async () => {
    setLoading(true)
    setIsConnected(false)
    try {
      const data = await api.connect(instanceName)
      setConnectData({
        base64: data?.base64 || data?.qrcode?.base64 || null,
        pairingCode: data?.pairingCode || null,
      })
    } catch { toast.error("Error al obtener QR") }
    finally { setLoading(false) }
  }

  useEffect(() => {
    if (!open || !instanceName) return
    fetchQR()
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.connectionState(instanceName)
        const state = parse.state(s)
        if (state === "open") {
          setIsConnected(true)
          clearInterval(pollRef.current)
          toast.success("WhatsApp conectado exitosamente")
          setTimeout(() => onOpenChange(false), 1500)
        }
      } catch { /* ignore */ }
    }, 3000)
    return () => { clearInterval(pollRef.current); setConnectData(null); setIsConnected(false) }
  }, [open, instanceName])

  const copyPairingCode = () => {
    if (connectData?.pairingCode) {
      navigator.clipboard.writeText(connectData.pairingCode)
      toast.success("Codigo copiado")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-base">
            <div className="h-11 w-11 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <QrCode className="h-6 w-6 text-emerald-500" />
            </div>
            <div>
              <p className="font-bold">Conectar WhatsApp</p>
              <p className="text-xs text-muted-foreground font-normal font-mono mt-0.5">{instanceName}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <AnimatePresence mode="wait">
            {isConnected ? (
              <motion.div
                key="connected"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center py-10"
              >
                <div className="h-20 w-20 rounded-3xl bg-emerald-500/10 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/10">
                  <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                </div>
                <p className="text-lg font-bold text-emerald-500">Conectado</p>
                <p className="text-sm text-muted-foreground mt-1">WhatsApp vinculado exitosamente</p>
              </motion.div>
            ) : loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center py-14"
              >
                <Loader2 className="h-10 w-10 animate-spin text-emerald-500 mb-4" />
                <p className="text-sm text-muted-foreground font-medium">Generando codigo QR...</p>
              </motion.div>
            ) : (
              <motion.div key="qr" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* QR Code */}
                {connectData?.base64 && (
                  <div className="flex flex-col items-center">
                    <div className="rounded-2xl border-2 border-emerald-500/20 bg-white p-4 shadow-lg shadow-emerald-500/5">
                      <img src={connectData.base64} alt="QR Code" className="w-56 h-56" />
                    </div>
                    <div className="mt-4 flex items-start gap-3 text-xs text-muted-foreground bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/50">
                      <Smartphone className="h-5 w-5 shrink-0 mt-0.5 text-emerald-500" />
                      <p className="leading-relaxed">Abre <b>WhatsApp</b> en tu telefono &gt; <b>Dispositivos vinculados</b> &gt; <b>Vincular dispositivo</b> &gt; Escanea este QR</p>
                    </div>
                  </div>
                )}

                {/* Pairing code */}
                {connectData?.pairingCode && (
                  <div className="space-y-2 mt-3">
                    <p className="text-xs text-muted-foreground text-center font-medium">O usa el codigo de vinculacion:</p>
                    <button
                      onClick={copyPairingCode}
                      className="w-full flex items-center justify-center gap-3 rounded-xl border-2 border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-950/20 py-4 px-4 transition-all hover:bg-emerald-50 dark:hover:bg-emerald-950/30 hover:border-emerald-500/40"
                    >
                      <span className="text-xl font-bold font-mono tracking-[0.3em] text-emerald-700 dark:text-emerald-400">{connectData.pairingCode}</span>
                      <Copy className="h-5 w-5 text-emerald-500/50" />
                    </button>
                  </div>
                )}

                {/* No QR fallback */}
                {!connectData?.base64 && !connectData?.pairingCode && (
                  <div className="flex flex-col items-center py-10">
                    <div className="h-16 w-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-3">
                      <AlertCircle className="h-8 w-8 text-amber-500" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">No se pudo obtener el QR</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Intenta regenerar el codigo</p>
                  </div>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchQR}
                  className="w-full h-10 gap-2 mt-3 rounded-xl font-medium"
                >
                  <RefreshCw className="h-4 w-4" /> Regenerar codigo QR
                </Button>

                {/* Compartir link público para escaneo remoto */}
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    const url = `${window.location.origin}/qr/${encodeURIComponent(instanceName)}`
                    navigator.clipboard.writeText(url)
                    toast.success("Link copiado al portapapeles", {
                      description: "Compártelo con quien deba escanear el QR",
                    })
                  }}
                  className="w-full h-10 gap-2 mt-2 rounded-xl font-medium bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                >
                  <Copy className="h-4 w-4" /> Copiar link público para compartir
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// =============================================
// IA GLOBAL TOGGLE CARD
// =============================================

function IAGlobalCard() {
  // estado agregado: todas pausadas, todas activas, o mixto
  const [estados, setEstados] = useState({}) // { ia_pausada_lulu: true, ... }
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    getIaPausadaAll()
      .then((v) => alive && setEstados(v))
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    const unsub = subscribeToIaPausadaAll((all) => alive && setEstados(all))
    return () => { alive = false; unsub() }
  }, [])

  const vals = Object.values(estados)
  const todasPausadas = vals.length > 0 && vals.every(v => v === true)
  const todasActivas = vals.length > 0 && vals.every(v => v === false)
  const mixto = vals.length > 0 && !todasPausadas && !todasActivas
  const pausada = todasPausadas

  const onToggle = async () => {
    if (saving) return
    // Si está mixto o todas pausadas → "reactivar todas"
    // Si todas activas → "pausar todas"
    const next = !todasPausadas ? true : false
    const confirmMsg = next
      ? "¿Pausar la IA en TODAS las instancias?\n\nLos mensajes se siguen guardando, pero el bot dejará de responder en Lulu, Gaby y Yair Demo."
      : "¿Reactivar la IA en TODAS las instancias?\n\nEl bot volverá a responder en Lulu, Gaby y Yair Demo. Si solo quieres activar una, usa el toggle de esa instancia."
    if (!window.confirm(confirmMsg)) return
    setSaving(true)
    try {
      await setIaPausadaAll(next)
      toast.success(next ? "IA pausada en todas las instancias" : "IA reactivada en todas las instancias")
    } catch (e) {
      toast.error(`No se pudo cambiar el estado: ${e.message || e}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800/80 bg-white/70 dark:bg-slate-900/40 backdrop-blur-md p-5">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-72 mt-2" />
      </div>
    )
  }

  // Conteo legible para estado mixto
  const activas = vals.filter(v => v === false).length
  const totalCount = vals.length

  // Colores y textos según estado: pausada (todas off) / activa (todas on) / mixto
  const colorClass = todasPausadas
    ? "border-amber-400/60 bg-gradient-to-br from-amber-50/90 to-orange-50/70 dark:from-amber-950/40 dark:to-orange-950/30 dark:border-amber-500/40"
    : todasActivas
      ? "border-emerald-400/60 bg-gradient-to-br from-emerald-50/90 to-teal-50/70 dark:from-emerald-950/40 dark:to-teal-950/30 dark:border-emerald-500/40"
      : "border-indigo-400/60 bg-gradient-to-br from-indigo-50/90 to-violet-50/70 dark:from-indigo-950/40 dark:to-violet-950/30 dark:border-indigo-500/40"
  const glowClass = todasPausadas ? "bg-amber-400/30" : todasActivas ? "bg-emerald-400/30" : "bg-indigo-400/30"
  const iconBgClass = todasPausadas
    ? "bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/40"
    : todasActivas
      ? "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/40"
      : "bg-gradient-to-br from-indigo-500 to-violet-600 shadow-indigo-500/40"
  const badgeClass = todasPausadas
    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30"
    : todasActivas
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
      : "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30"
  const badgeDot = todasPausadas ? "bg-amber-500 animate-pulse" : todasActivas ? "bg-emerald-500" : "bg-indigo-500 animate-pulse"
  const badgeLabel = todasPausadas ? "Pausada" : todasActivas ? "Activa" : "Mixto"

  const descripcion = todasPausadas
    ? "Ninguna instancia tiene IA activa. Los mensajes se siguen guardando en el CRM y se atienden manualmente."
    : todasActivas
      ? "Las 3 instancias tienen IA activa. El bot responde automáticamente en Lulu, Gaby y Brain (Yair Demo)."
      : `${activas} de ${totalCount} instancias tienen IA activa. Usa el toggle individual de cada card para ajustar.`

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn("relative rounded-2xl border-2 p-5 backdrop-blur-md overflow-hidden", colorClass)}
    >
      {/* Glow */}
      <div className={cn("absolute -top-12 -right-12 h-40 w-40 rounded-full blur-3xl pointer-events-none", glowClass)} />

      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center shadow-lg shrink-0", iconBgClass)}>
            {todasPausadas ? <Pause className="h-6 w-6 text-white" /> : <Bot className="h-6 w-6 text-white" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
                IA Conversacional (Global)
              </h3>
              <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider", badgeClass)}>
                <span className={cn("h-1.5 w-1.5 rounded-full", badgeDot)} />
                {badgeLabel}
              </span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">
              {descripcion}
            </p>
          </div>
        </div>

        <Button
          onClick={onToggle}
          disabled={saving}
          size="lg"
          className={cn(
            "gap-2 h-12 px-6 rounded-xl font-bold shrink-0 shadow-md transition-all",
            todasActivas
              ? "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-amber-500/30"
              : "bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-emerald-500/30"
          )}
        >
          {saving ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : todasActivas ? (
            <>
              <Pause className="h-5 w-5" />
              Pausar todas
            </>
          ) : (
            <>
              <Bot className="h-5 w-5" />
              Reactivar todas
            </>
          )}
        </Button>
      </div>
    </motion.div>
  )
}

// =============================================
// MAIN PAGE
// =============================================

export function EvolutionPanel() {
  const navigate = useNavigate()
  const [instances, setInstances] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [connectingInstance, setConnectingInstance] = useState("")

  const loadInstances = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.fetchInstances()
      const list = Array.isArray(data) ? data : []

      const enriched = await Promise.all(
        list.map(async (inst) => {
          const name = parse.name(inst)
          if (!name) return { name: "(sin nombre)", state: "unknown" }

          let state = parse.state(inst)
          if (!state) {
            try { state = parse.state(await api.connectionState(name)) || "unknown" }
            catch { state = "error" }
          }

          const owner = parse.owner(inst)
          let profilePic = parse.pic(inst)

          if (!profilePic && state === "open") {
            if (owner) {
              profilePic = await api.profilePic(name, owner)
            }
            if (!profilePic) {
              profilePic = await api.profilePic(name, name)
            }
          }

          const apiCounts = parse.counts(inst)
          const counts = apiCounts ? {
            msgs: apiCounts.Message ?? apiCounts.messages ?? 0,
            contacts: apiCounts.Contact ?? apiCounts.contacts ?? 0,
            chats: apiCounts.Chat ?? apiCounts.chats ?? 0,
          } : null

          return {
            name,
            state,
            owner,
            profilePic,
            profileName: parse.profileName(inst),
            integration: parse.integration(inst),
            counts,
            onRefresh: loadInstances,
          }
        })
      )
      setInstances(enriched)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadInstances() }, [loadInstances])

  const connectedCount = instances.filter(i => i.state === "open").length
  const totalCount = instances.length

  return (
    <ProtectedRoute requireSuperAdmin={true}>
      {({ onLogout }) => (
        <Sidebar onLogout={onLogout}>
          <div className="relative min-h-full overflow-hidden">

            {/* ── Fondo decorativo: mesh gradient sutil + dot pattern ── */}
            <div className="absolute inset-0 -z-10 pointer-events-none">
              {/* Blobs sutiles — solo decoración de esquinas, no atraviesan las cards */}
              <div className="absolute -top-40 -left-40 h-80 w-80 rounded-full bg-emerald-500/10 dark:bg-emerald-500/8 blur-[100px] animate-pulse" style={{ animationDuration: "8s" }} />
              <div className="absolute top-40 -right-40 h-80 w-80 rounded-full bg-teal-400/8 dark:bg-teal-500/6 blur-[100px] animate-pulse" style={{ animationDuration: "10s", animationDelay: "1s" }} />
              <div className="absolute -bottom-40 left-1/4 h-72 w-72 rounded-full bg-green-500/8 dark:bg-green-500/6 blur-[100px]" />

              {/* Patrón de puntos muy sutil */}
              <div
                className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
                style={{
                  backgroundImage: "radial-gradient(circle, currentColor 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                  color: "#10b981",
                }}
              />

              {/* Línea de degradado horizontal en la parte superior */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
            </div>

            <div className="relative space-y-5 p-4 md:p-6 max-w-[1200px] mx-auto">

            {/* ── Header con glow ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="absolute inset-0 rounded-2xl bg-emerald-500/40 blur-xl animate-pulse" style={{ animationDuration: "3s" }} />
                  <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-600 shadow-xl shadow-emerald-500/40 ring-1 ring-emerald-300/40">
                    <WhatsappLogo className="h-7 w-7 text-white drop-shadow-md" />
                  </div>
                  {connectedCount > 0 && (
                    <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 border-2 border-background flex items-center justify-center shadow-lg shadow-emerald-500/50 animate-pulse">
                      <span className="text-[9px] font-black text-white">{connectedCount}</span>
                    </div>
                  )}
                </div>
                <div>
                  <h1 className="text-2xl font-black tracking-tight bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 dark:from-emerald-400 dark:via-teal-300 dark:to-emerald-500 bg-clip-text text-transparent">
                    Evolution API
                  </h1>
                  <p className="text-xs text-muted-foreground/80 mt-0.5 flex items-center gap-1.5">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Gestiona tus instancias de WhatsApp
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={loadInstances} className="h-8 gap-1.5 rounded-lg font-medium text-xs px-3">
                  <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                  <span className="hidden sm:inline">Actualizar</span>
                </Button>
                <Button size="sm" onClick={() => setShowCreate(true)} className="h-8 gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm shadow-emerald-600/20 font-semibold text-xs px-3">
                  <Plus className="h-3.5 w-3.5" />
                  Nueva instancia
                </Button>
              </div>
            </div>

            {/* ── Toggle global de la IA ── */}
            <IAGlobalCard />

            {/* ── Summary bar — pills glass con fondo translúcido ── */}
            {instances.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 flex-wrap"
              >
                <div className="inline-flex items-center gap-2 rounded-xl border border-white/40 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 backdrop-blur-md px-3.5 py-2 shadow-sm">
                  <Globe2 className="h-4 w-4 text-slate-400" />
                  <span className="text-xs text-muted-foreground font-medium">Total</span>
                  <span className="text-sm font-bold font-mono">{totalCount}</span>
                </div>
                <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-50/70 dark:bg-emerald-950/30 backdrop-blur-md px-3.5 py-2 shadow-sm shadow-emerald-500/10">
                  <Zap className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold">Conectadas</span>
                  <span className="text-sm font-bold font-mono text-emerald-700 dark:text-emerald-300">{connectedCount}</span>
                </div>
                {(totalCount - connectedCount) > 0 && (
                  <div className="inline-flex items-center gap-2 rounded-xl border border-red-500/25 bg-red-50/70 dark:bg-red-950/30 backdrop-blur-md px-3.5 py-2 shadow-sm">
                    <PlugDisconnected className="h-4 w-4 text-red-400" />
                    <span className="text-xs text-red-600 dark:text-red-400 font-semibold">Desconectadas</span>
                    <span className="text-sm font-bold font-mono text-red-600 dark:text-red-400">{totalCount - connectedCount}</span>
                  </div>
                )}
              </motion.div>
            )}

            {/* ── Error ── */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-5 rounded-2xl border border-red-500/30 bg-red-500/5 flex items-start gap-4"
              >
                <div className="h-10 w-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-red-500">Error de conexion</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadInstances}
                    className="mt-3 h-8 gap-2 rounded-lg text-xs border-red-500/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> Reintentar
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── Content ── */}
            {loading ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="rounded-2xl border border-border/30 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <Skeleton className="h-12 w-12 rounded-xl" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                    <div className="flex gap-1.5">
                      <Skeleton className="h-6 w-24 rounded-lg" />
                      <Skeleton className="h-6 w-16 rounded-lg" />
                      <Skeleton className="h-6 w-16 rounded-lg" />
                    </div>
                    <Skeleton className="h-7 w-full rounded-lg" />
                  </div>
                ))}
              </div>
            ) : !error && instances.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center py-16 text-muted-foreground/30"
              >
                <div className="h-16 w-16 rounded-2xl bg-emerald-500/5 flex items-center justify-center mb-4 border-2 border-dashed border-emerald-500/15">
                  <WhatsappLogo className="h-8 w-8 text-emerald-500/25" />
                </div>
                <p className="text-sm font-semibold text-muted-foreground/50">Sin instancias</p>
                <p className="text-xs mt-1 text-muted-foreground/30">Crea tu primera instancia de WhatsApp</p>
                <Button size="sm" className="mt-4 h-8 gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm shadow-emerald-600/20 font-semibold text-xs px-4" onClick={() => setShowCreate(true)}>
                  <Plus className="h-3.5 w-3.5" /> Crear instancia
                </Button>
              </motion.div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {instances.map((inst) => (
                  <InstanceCard key={inst.name} instance={inst} onConnect={setConnectingInstance} />
                ))}
              </div>
            )}

            {/* Dialogs */}
            <CreateInstanceDialog
              open={showCreate}
              onOpenChange={setShowCreate}
              onCreated={() => loadInstances()}
            />
            <ConnectDialog
              instanceName={connectingInstance}
              open={!!connectingInstance}
              onOpenChange={(open) => { if (!open) { setConnectingInstance(""); loadInstances() } }}
            />
            </div>
          </div>
        </Sidebar>
      )}
    </ProtectedRoute>
  )
}
